import { Component, OnInit, ViewChild } from '@angular/core';
import {JsonCase, TongueDiagnosisDto, UploadedCase} from 'src/model/dto';
import { ImageUploaderComponent } from '../component/image-uploader/image-uploader.component';
import { MessageService } from 'primeng/api';
import { SeverityEnum, ToastMsg } from 'src/model/page';
import { AppService } from '../app.service';
import {DateUtility} from "../utility/DateUtility";
import { firstValueFrom } from 'rxjs';


@Component({
  selector: 'app-user-input',
  templateUrl: './user-input.component.html',
  styleUrls: ['./user-input.component.scss']
})
export class UserInputComponent implements OnInit {
  @ViewChild(ImageUploaderComponent) imageUploader: ImageUploaderComponent;
  inputData: UploadedCase;
  diagnosisResults: TongueDiagnosisDto[] | null = null;
  diagnosisImageUrl = '';
  patientNames: string[] = [];
  filteredNames: string[] = [];

  constructor(
    private appService: AppService,
    private msgService: MessageService
  ) { }

  ngOnInit(): void {
    this.reset();
    this.loadPatientNames();
  }

  async loadPatientNames(): Promise<void> {
    try {
      const names: string[] = await firstValueFrom(this.appService.get('data', 'getPatientNames'));
      this.patientNames = names || [];
    } catch (error) {
      console.error('Failed to load patient names', error);
    }
  }

  filterNames(event: { query: string }): void {
    const query = (event.query || '').trim().toLowerCase();
    if (!query) {
      this.filteredNames = [...this.patientNames];
    } else {
      this.filteredNames = this.patientNames.filter(name =>
        name.toLowerCase().includes(query)
      );
    }
  }

  reset(): void {
    this.inputData = new UploadedCase();
    this.inputData.createTime = DateUtility.toFormatString(new Date(), 'YYYY/MM/DD hh:mm:ss');
    setTimeout(() => {
      this.imageUploader.reset();
    }, 200);
  }

  async uploadCase(): Promise<boolean> {
    const datetime = new Date(this.inputData.createTime);
    if (isNaN(datetime.getTime())) {
      this.msgService.add(new ToastMsg(SeverityEnum.error, 'Create Time Format Error', 'Please input the correct time, e.g. 2024/10/01 00:00:00'));
      return false;
    }
    this.inputData.createTime = DateUtility.toFormatString(datetime, 'YYYY/MM/DD hh:mm:ss');
    if (!this.imageUploader.Image) {
      this.msgService.add(new ToastMsg(SeverityEnum.error, 'Fail to upload the case', 'Please take a photo of tongue or upload one.'));
      return false;
    }
    this.inputData.photo = this.imageUploader.Image;
    try {
      await firstValueFrom(this.appService.postForm('data', 'uploadCase', this.inputData));
      return true;
    } catch (error) {
      console.error(error);
      this.msgService.add(new ToastMsg(SeverityEnum.error, 'Fail to upload the case', error.toString()));
      return false;
    }
  }

  async upload(): Promise<void> {
    this.appService.setBlock(true);
    const isSuccess = await this.uploadCase();
    if (isSuccess) {
      this.reset();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    this.appService.setBlock(false);
  }

  async diagnose(): Promise<void> {
    this.appService.setBlock(true);
    const isSuccess = await this.uploadCase();
    if (isSuccess) {
      this.diagnosisImageUrl = this.imageUploader.DisplayUrl;
      const params = {
        photo: this.imageUploader.Image,
        gender: this.inputData.gender
      };
      try {
        this.diagnosisResults = await firstValueFrom(this.appService.postForm('data', 'diagnoseTongue', params));
      } catch (error) {
        console.error(error);
        this.msgService.add(new ToastMsg(SeverityEnum.error, 'Fail to diagnose the photo', error.toString()));
      }
    }
    this.appService.setBlock(false);
  }

  closeDiagnosisDisplayer(): void {
    this.diagnosisResults = null;
  }
}
